import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Paper,
  Chip,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';

export interface UploadProgressEvent {
  type: 'start' | 'progress' | 'row-start' | 'row-success' | 'row-error' | 'complete' | 'error';
  data: any;
}

export interface UploadProgressDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
}

export interface UploadProgressDialogRef {
  handleEvent: (event: UploadProgressEvent) => void;
}

interface LogEntry {
  id: string;
  type: 'info' | 'success' | 'error';
  message: string;
  timestamp: Date;
  rowNumber?: number;
}

interface UploadStats {
  total: number;
  current: number;
  success: number;
  errors: number;
}

export const UploadProgressDialog = React.forwardRef<
  UploadProgressDialogRef,
  UploadProgressDialogProps
>(({
  open,
  onClose,
  title,
}, ref) => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [stats, setStats] = useState<UploadStats>({
    total: 0,
    current: 0,
    success: 0,
    errors: 0,
  });
  const [isComplete, setIsComplete] = useState(false);
  const [finalReport, setFinalReport] = useState<any>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);

  const addLog = (type: LogEntry['type'], message: string, rowNumber?: number) => {
    const newLog: LogEntry = {
      id: `${Date.now()}-${Math.random()}`,
      type,
      message,
      timestamp: new Date(),
      rowNumber,
    };
    setLogs((prev) => [...prev, newLog]);
  };

  // Auto-scroll to bottom when logs update
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const handleReset = () => {
    setLogs([]);
    setStats({ total: 0, current: 0, success: 0, errors: 0 });
    setIsComplete(false);
    setFinalReport(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  // Expose method to handle SSE events (to be called by parent component)
  const handleEvent = useCallback((event: UploadProgressEvent) => {
    const { type, data } = event;

    switch (type) {
      case 'start':
        addLog('info', data.message || 'Starting upload...');
        if (data.total) {
          setStats((prev) => ({ ...prev, total: data.total }));
        }
        break;

      case 'progress':
        addLog('info', data.message || 'Processing...');
        if (data.total) {
          setStats((prev) => ({ ...prev, total: data.total }));
        }
        break;

      case 'row-start':
        addLog('info', data.message || `Processing row ${data.rowNumber}...`, data.rowNumber);
        if (data.current) {
          setStats((prev) => ({ ...prev, current: data.current }));
        }
        break;

      case 'row-success':
        addLog('success', data.message || `Row ${data.rowNumber} processed successfully`, data.rowNumber);
        setStats((prev) => ({ ...prev, success: prev.success + 1, current: data.current || prev.current }));
        break;

      case 'row-error':
        addLog('error', `Row ${data.rowNumber}: ${data.error}`, data.rowNumber);
        setStats((prev) => ({ ...prev, errors: prev.errors + 1, current: data.current || prev.current }));
        break;

      case 'complete':
        addLog('success', data.message || 'Upload completed!');
        setIsComplete(true);
        if (data.results) {
          setFinalReport(data.results);
          setStats((prev) => ({
            total: data.results.total || prev.total,
            current: data.results.total || prev.total,
            success: data.results.successCount || data.results.created + data.results.updated || prev.success,
            errors: data.results.errorCount || data.results.errors?.length || prev.errors,
          }));
        }
        break;

      case 'error':
        addLog('error', data.message || 'An error occurred');
        setIsComplete(true);
        break;

      default:
        console.warn('Unknown event type:', type);
    }
  }, []);

  // Expose handleEvent method via ref (parent component can call it)
  React.useImperativeHandle(
    ref,
    () => ({
      handleEvent,
    }),
    [handleEvent]
  );

  const progress = stats.total > 0 ? (stats.current / stats.total) * 100 : 0;

  return (
    <Dialog 
      open={open} 
      onClose={isComplete ? handleClose : undefined}
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown={!isComplete}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Progress Bar */}
          {!isComplete && (
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Progress: {stats.current} / {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {progress.toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          )}

          {/* Statistics */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip 
              label={`Total: ${stats.total}`} 
              size="small" 
              color="default"
            />
            <Chip 
              label={`Success: ${stats.success}`} 
              size="small" 
              color="success"
              icon={<CheckCircleIcon />}
            />
            {stats.errors > 0 && (
              <Chip 
                label={`Errors: ${stats.errors}`} 
                size="small" 
                color="error"
                icon={<ErrorIcon />}
              />
            )}
          </Box>

          <Divider />

          {/* Final Report */}
          {isComplete && finalReport && (
            <Alert 
              severity={finalReport.errorCount > 0 ? 'warning' : 'success'}
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Upload Complete
              </Typography>
              <Typography variant="body2">
                Total Rows: {finalReport.total} | 
                Success: {finalReport.successCount || (finalReport.created + finalReport.updated)} | 
                Errors: {finalReport.errorCount || finalReport.errors?.length || 0}
              </Typography>
              {finalReport.created !== undefined && (
                <Typography variant="body2">
                  Created: {finalReport.created} | Updated: {finalReport.updated}
                </Typography>
              )}
            </Alert>
          )}

          {/* Logs */}
          <Paper
            variant="outlined"
            sx={{
              maxHeight: 400,
              minHeight: 200,
              overflow: 'auto',
              p: 2,
              backgroundColor: '#fafafa',
            }}
          >
            {logs.length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center">
                No logs yet...
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {logs.map((log) => (
                  <Box
                    key={log.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      fontSize: '0.875rem',
                      fontFamily: 'monospace',
                    }}
                  >
                    {log.type === 'success' && (
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main', mt: 0.2 }} />
                    )}
                    {log.type === 'error' && (
                      <ErrorIcon sx={{ fontSize: 16, color: 'error.main', mt: 0.2 }} />
                    )}
                    {log.type === 'info' && (
                      <InfoIcon sx={{ fontSize: 16, color: 'info.main', mt: 0.2 }} />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        color:
                          log.type === 'success'
                            ? 'success.dark'
                            : log.type === 'error'
                            ? 'error.dark'
                            : 'text.primary',
                        flex: 1,
                      }}
                    >
                      {log.rowNumber && `[Row ${log.rowNumber}] `}
                      {log.message}
                    </Typography>
                  </Box>
                ))}
                <div ref={logsEndRef} />
              </Box>
            )}
          </Paper>

          {/* Error Details */}
          {isComplete && finalReport && finalReport.errors && finalReport.errors.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="error" gutterBottom>
                Errors ({finalReport.errors.length}):
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                  p: 2,
                  backgroundColor: '#fff5f5',
                }}
              >
                {finalReport.errors.slice(0, 20).map((err: any, idx: number) => (
                  <Typography
                    key={idx}
                    variant="caption"
                    component="div"
                    color="error"
                    sx={{ fontFamily: 'monospace', mb: 0.5 }}
                  >
                    Row {err.row}: {err.error}
                  </Typography>
                ))}
                {finalReport.errors.length > 20 && (
                  <Typography variant="caption" color="error">
                    ... and {finalReport.errors.length - 20} more errors
                  </Typography>
                )}
              </Paper>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {isComplete ? (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Processing... Please wait
          </Typography>
        )}
      </DialogActions>
    </Dialog>
  );
});

UploadProgressDialog.displayName = 'UploadProgressDialog';

// Hook to manage SSE connection
export const useSSEUpload = (
  url: string,
  onEvent: (event: UploadProgressEvent) => void,
  onError?: (error: any) => void
) => {
  const eventSourceRef = useRef<EventSource | null>(null);

  const startUpload = (formData: FormData) => {
    return new Promise<void>((resolve, reject) => {
      // First, send the upload request
      fetch(url, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Upload failed: ${response.statusText}`);
          }

          // Create EventSource to receive SSE events
          const reader = response.body?.getReader();
          const decoder = new TextDecoder();

          if (!reader) {
            throw new Error('No response body');
          }

          const readStream = async () => {
            try {
              while (true) {
                const { done, value } = await reader.read();
                if (done) {
                  resolve();
                  break;
                }

                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split('\n');

                let currentEvent = '';
                let currentData = '';

                for (const line of lines) {
                  if (line.startsWith('event:')) {
                    currentEvent = line.substring(6).trim();
                  } else if (line.startsWith('data:')) {
                    currentData = line.substring(5).trim();
                  } else if (line === '' && currentEvent && currentData) {
                    // Complete event received
                    try {
                      const data = JSON.parse(currentData);
                      onEvent({ type: currentEvent as any, data });
                    } catch (e) {
                      console.error('Failed to parse SSE data:', e);
                    }
                    currentEvent = '';
                    currentData = '';
                  }
                }
              }
            } catch (error) {
              console.error('Stream reading error:', error);
              if (onError) onError(error);
              reject(error);
            }
          };

          readStream();
        })
        .catch((error) => {
          console.error('Upload error:', error);
          if (onError) onError(error);
          reject(error);
        });
    });
  };

  const cleanup = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  };

  useEffect(() => {
    return cleanup;
  }, []);

  return { startUpload, cleanup };
};

export default UploadProgressDialog;
